import { useState, useMemo, useRef, useEffect } from 'react'
import { GTooltip } from 'components/basic-blocks'
import { GNewToggle } from 'components/basic-blocks/g-new-toggle/g-new-toggle'
import { FiArrowLeft, FiHelpCircle, FiEdit2 } from 'react-icons/fi'
import { CopyButton } from 'components/copy-button/copy-button'
import { CampaignTrackingUpdatePreview, Platform } from './types'
import { SelectedAccountBanner } from './selected-account-banner'
import { TrackerApiConfig } from 'config'
import {
	AppButton,
	ListItem,
	MenuList,
	MenuListDivider
} from '@clickguard/design-system'

interface TrackingConfirmationViewProps {
	platform: Platform
	existingTT: string | null
	existingUrlSuffix: string | null
	campaignsToUpdate: CampaignTrackingUpdatePreview[]
	adAccountNumber: string
	accountName?: string
	loading: boolean
	error: string | null
	onBack: () => void
	onConfirm: (params: {
		useClickGuardTT: boolean
		customUrlSuffix: string
	}) => void
}

/**
 * Generate the ClickGuard suffix portion.
 * Format: tm={value}&ap={platform}&aaid={ad_account_id}&cid=...&agid=...
 * Note: aaid will be replaced with the actual ad account ID after creation on the backend.
 * Here we use the account number as a placeholder for display purposes.
 */
const generateClickGuardSuffix = (
	tm: 'tt' | 'tc',
	platform: Platform
): string => {
	const ap = platform === 'GADS' ? 'gads' : 'bads'

	if (platform === 'GADS') {
		return (
			`tm=${tm}&ap=${ap}` +
			'&aaid={aaid}&cid={campaignid}&agid={adgroupid}&aid={creative}&kid={targetid}&kw={keyword}' +
			'&nw={network}&dv={device}&mt={matchtype}&pl={placement}'
		)
	}

	// BADS
	return (
		`tm=${tm}&ap=${ap}` +
		'&aaid={aaid}&cid={CampaignId}&cn={Campaign}&agid={AdGroupId}&agn={AdGroup}&aid={AdId}&kid={TargetId}&kw={keyword:default}' +
		'&nw={Network}&dv={Device}&mt={MatchType}'
	)
}

/**
 * Generate ClickGuard's tracking template for display purposes.
 */
const generateDisplayTrackingTemplate = (platform: Platform): string => {
	const baseUrl = TrackerApiConfig.API_BASE_URL

	if (platform === 'GADS') {
		return (
			`${baseUrl}/gads/at/{aid}/{asid}/{aaid}` +
			'?tm=tt&cid={campaignid}&agid={adgroupid}&aid={creative}&kid={targetid}&kw={keyword}' +
			'&nw={network}&dv={device}&mt={matchtype}&pl={placement}&url={lpurl}'
		)
	}

	return (
		`${baseUrl}/bads/at/{aid}/{asid}/{aaid}` +
		'?tm=tt&cid={CampaignId}&cn={Campaign}&agid={AdGroupId}&agn={AdGroup}&aid={AdId}&kid={TargetId}&kw={keyword:default}' +
		'&nw={Network}&dv={Device}&mt={MatchType}&url={lpurl}'
	)
}

export const TrackingConfirmationView = ({
	platform,
	existingTT,
	existingUrlSuffix,
	campaignsToUpdate,
	adAccountNumber,
	accountName,
	loading,
	error,
	onBack,
	onConfirm
}: TrackingConfirmationViewProps) => {
	const hasExistingTT = !!existingTT
	const hasExistingSuffix = !!existingUrlSuffix

	// Cases 3 & 4: show toggle when existing TT is present
	const showToggle = hasExistingTT
	const [useClickGuardTT, setUseClickGuardTT] = useState(!hasExistingTT)
	const [campaignListOpen, setCampaignListOpen] = useState(false)
	const campaignListRef = useRef<HTMLDivElement>(null)

	// URL suffix editing state
	const [suffixLocked, setSuffixLocked] = useState(true)
	const [editedExistingSuffix, setEditedExistingSuffix] = useState<
		string | null
	>(null)
	const editableRef = useRef<HTMLSpanElement>(null)
	const suffixEdited =
		editedExistingSuffix !== null &&
		editedExistingSuffix !== (existingUrlSuffix ?? '')

	// tm value: 'tt' if using ClickGuard's TT, 'tc' if keeping client's
	const tmValue = useClickGuardTT ? 'tt' : 'tc'

	// Compute the ClickGuard suffix portion
	const clickGuardSuffix = useMemo(
		() => generateClickGuardSuffix(tmValue as 'tt' | 'tc', platform),
		[tmValue, platform]
	)

	// Compute the full URL suffix
	const fullSuffix = useMemo(() => {
		const prefix = editedExistingSuffix ?? existingUrlSuffix
		return prefix
	}, [existingUrlSuffix, editedExistingSuffix])

	// When unlocking, seed the contentEditable span imperatively (once) so React
	// doesn't reset the cursor on every keystroke, then move the caret to the end.
	useEffect(() => {
		if (suffixLocked || !editableRef.current) return
		editableRef.current.innerText =
			editedExistingSuffix ?? existingUrlSuffix ?? ''
		const range = document.createRange()
		range.selectNodeContents(editableRef.current)
		range.collapse(false)
		const sel = window.getSelection()
		sel?.removeAllRanges()
		sel?.addRange(range)
		editableRef.current.focus()
	}, [suffixLocked])

	useEffect(() => {
		if (!campaignListOpen) return

		const handleClickOutside = (event: MouseEvent) => {
			if (
				campaignListRef.current &&
				!campaignListRef.current.contains(event.target as Node)
			) {
				setCampaignListOpen(false)
			}
		}

		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				setCampaignListOpen(false)
			}
		}

		document.addEventListener('mousedown', handleClickOutside)
		document.addEventListener('keydown', handleKeyDown)
		return () => {
			document.removeEventListener('mousedown', handleClickOutside)
			document.removeEventListener('keydown', handleKeyDown)
		}
	}, [campaignListOpen])

	// Compute the display tracking template
	const displayTT = useMemo(() => {
		if (showToggle && !useClickGuardTT) {
			return existingTT || ''
		}
		return generateDisplayTrackingTemplate(platform)
	}, [showToggle, useClickGuardTT, existingTT, platform])

	// Determine tracking template helper text
	const ttHelperText = hasExistingTT
		? 'You already have a tracking template in place:'
		: 'Previously empty. ClickGuard will set this.'

	// Determine URL suffix helper text
	const suffixHelperText = hasExistingSuffix
		? "Your existing parameters will stay. ClickGuard's will go at the end."
		: "No tracking parameters are currently set. We'll automatically add the recommended tracking parameters below."

	const handleConfirm = () => {
		onConfirm({
			useClickGuardTT,
			customUrlSuffix: fullSuffix ?? ''
		})
	}

	const handleSuffixUnlock = () => {
		setSuffixLocked(false)
	}

	const handleSuffixLock = () => {
		setSuffixLocked(true)
	}

	return (
		<div className="flex flex-col gap-[32px]">
			{/* Header */}
			<div className="flex flex-col gap-[16px]">
				<button
					type="button"
					onClick={onBack}
					className="flex w-fit items-center gap-[8px] rounded-full text-[16px] font-semibold leading-[22px] text-t-desc-gray hover:text-t-default"
				>
					<FiArrowLeft className="h-5 w-5" />
					Back
				</button>
				<div className="flex flex-col gap-[16px]">
					<h1 className="whitespace-nowrap text-[24px] font-semibold leading-[30px] text-t-default">
						Connect your {platform === 'GADS' ? 'Google Ads' : 'Microsoft Ads'}{' '}
						account
					</h1>
					<p className="text-[16px] leading-[22px] text-t-checkbox-unactive">
						This is the tracking setup we recommend for your account:
					</p>
				</div>
			</div>

			{/* Cards section */}
			<div className="flex flex-col gap-[24px]">
				{/* Account Banner */}
				{(accountName || adAccountNumber) && (
					<SelectedAccountBanner
						platform={platform}
						accountName={
							accountName ||
							(platform === 'GADS'
								? 'Google Ads Account'
								: 'Microsoft Ads Account')
						}
						accountNumber={adAccountNumber}
					/>
				)}

				{/* Tracking Template Card */}
				<div className="bg-white border border-t-checkbox rounded-[8px] shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)] overflow-hidden px-[16px] py-[14px] flex flex-col gap-[12px]">
					<div className="flex flex-col gap-[8px]">
						<div className="flex items-center gap-[8px]">
							<span className="text-[16px] leading-[22px] font-medium text-t-google-ad">
								Tracking template
							</span>

							<GTooltip
								position="top"
								arrowClassName="w-0 h-0 absolute bottom-[0.5px] border-l-8 border-l-transparent  border-r-8 border-r-transparent border-t-8 border-t-tooltip-caret"
								contentClassName="border-none  mb-2 bg-t-smoke max-w-xs font-medium text-sm text-t-desc-gray"
								content={
									<p>
										This is the URL that processes your ad clicks for tracking.
										<br />
										<br />
										You can keep your current template, but it may reduce click
										tracking accuracy. In that case, ClickGuard will use Final
										URL parameters to detect and analyze ad clicks.
										<br />
										<br />
										Using ClickGuard’s Tracking Template ensures the most
										reliable results. If you choose to use ours, make sure to
										disable any automated process that could overwrite it.
									</p>
								}
							>
								<FiHelpCircle className="w-[16px] h-[16px] text-t-gray-hold" />
							</GTooltip>
						</div>
						<span className="text-[14px] leading-[18px] text-t-desc-gray">
							{ttHelperText}
						</span>
					</div>
					<div className="bg-bg-plan-fill rounded-[8px] px-[14px] py-[10px] flex items-center gap-[8px]">
						<p className="flex-1 text-[16px] leading-[22px] text-t-gray-checkout overflow-hidden text-ellipsis whitespace-nowrap min-w-0">
							{displayTT}
						</p>
						{!!displayTT && <CopyButton value={displayTT} />}
					</div>
					{showToggle && (
						<>
							<div className="flex items-center gap-[12px]">
								<span className="flex-1 text-[14px] leading-[18px] text-t-desc-gray">
									Keep it, or toggle on to use ClickGuard&apos;s template for
									the most accurate click fraud detection.
								</span>
								<GNewToggle
									checked={useClickGuardTT}
									onChange={(v) => setUseClickGuardTT(v)}
								/>
							</div>
							<p className="text-[14px] italic leading-[18px] text-t-gray-hold">
								(Optional, but recommended)
							</p>
						</>
					)}
				</div>

				{/* URL Suffix Card */}
				<div className="bg-white border border-t-checkbox rounded-[8px] shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)] overflow-hidden p-[16px] flex flex-col gap-[12px]">
					<div className="flex flex-col gap-[8px]">
						<div className="flex items-center gap-[8px]">
							<span className="text-[16px] leading-[22px] font-medium text-t-google-ad">
								Final URL suffix
							</span>
							<GTooltip
								position="top"
								arrowClassName="w-0 h-0 absolute bottom-[0.5px] border-l-8 border-l-transparent  border-r-8 border-r-transparent border-t-8 border-t-tooltip-caret"
								contentClassName="border-none  mb-2 bg-t-smoke max-w-xs font-medium text-sm text-t-desc-gray"
								content={
									<p>
										Parameters that the ad platform automatically adds to the
										end of your URL when someone clicks on your ad.
										<br />
										<br />
										Example: www.yourwebsite.com/?param1=value1&param2=value2
										<br />
										<br />
										Including ClickGuard’s parameters is mandatory for
										successful ad click tracking.
									</p>
								}
							>
								<FiHelpCircle className="w-[16px] h-[16px] text-t-gray-hold" />
							</GTooltip>
						</div>
						<p className="text-[14px] leading-[18px] text-t-desc-gray">
							{suffixHelperText}
						</p>
					</div>
					<div className="bg-bg-plan-fill rounded-[8px] px-[14px] py-[10px]">
						{suffixLocked ? (
							<div className="flex gap-[6px] items-start">
								<div className="flex-1 min-w-0">
									<p className="text-[16px] leading-[22px] break-all">
										{existingUrlSuffix && (
											<span className="text-t-gray-hold">
												{existingUrlSuffix}
											</span>
										)}
										<span className="text-[#8f88ff]">
											{existingUrlSuffix
												? `&${clickGuardSuffix}`
												: clickGuardSuffix}
										</span>
									</p>
								</div>
								<CopyButton
									value={
										existingUrlSuffix
											? `${existingUrlSuffix}&${clickGuardSuffix}`
											: clickGuardSuffix
									}
								/>
								<button
									onClick={handleSuffixUnlock}
									className="shrink-0 p-[4px] rounded-[6px] cursor-pointer"
								>
									<FiEdit2 className="w-[16px] h-[16px] text-t-gray-hold hover:text-brand" />
								</button>
							</div>
						) : (
							<div className="flex gap-[6px] items-start">
								<p className="flex-1 min-w-0 text-[16px] leading-[22px] break-all">
									<span
										ref={editableRef}
										contentEditable
										suppressContentEditableWarning
										onInput={() =>
											setEditedExistingSuffix(
												editableRef.current?.innerText ?? ''
											)
										}
										className="text-t-coupon focus:outline-none"
									/>
									<span className="text-[#8f88ff]" contentEditable={false}>
										{editedExistingSuffix ?? existingUrlSuffix
											? `&${clickGuardSuffix}`
											: clickGuardSuffix}
									</span>
								</p>
								<button
									onClick={handleSuffixLock}
									className="shrink-0 p-[4px] rounded-[6px] cursor-pointer"
								>
									<FiEdit2 className="w-[16px] h-[16px] text-brand" />
								</button>
							</div>
						)}
					</div>
					{suffixEdited && (
						<p className="text-[14px] leading-[18px] text-[#f59e0b]">
							You&apos;ve edited the URL suffix. The custom value will be used
							as-is.
						</p>
					)}
					<div className="relative flex items-center justify-between gap-[12px]">
						<p className="min-w-0 flex-1 text-[12px] text-t-gray-hold ">
							Campaigns with their own tracking parameters will be updated
							automatically
						</p>
						<div ref={campaignListRef} className="relative shrink-0">
							<AppButton
								label="See all"
								onClick={() => setCampaignListOpen((open) => !open)}
								variant="secondary"
								size="mini"
							/>
							{campaignListOpen && (
								<div className="absolute right-0 bottom-[30px] z-50">
									<MenuList
										className="flex w-[280px] flex-col"
										role="listbox"
										title="Campaigns"
									>
										<ListItem
											label="Campaigns"
											state="title"
											weight="medium"
											className="cg-cursor-default hover:cg-bg-transparent hover:cg-cursor-default"
										/>
										<MenuListDivider />
										<div className="max-h-[120px] overflow-y-auto">
											{campaignsToUpdate.length > 0 ? (
												campaignsToUpdate.map((campaign) => (
													<ListItem
														key={campaign.id}
														as="div"
														label={campaign.name}
														title={campaign.name}
														className="cg-cursor-default hover:cg-bg-transparent hover:cg-cursor-default"
													/>
												))
											) : (
												<ListItem
													key="no-campaigns"
													as="div"
													label="No campaigns need updates"
													title="No campaigns need updates"
													className="cg-cursor-default hover:cg-bg-transparent hover:cg-cursor-default"
												/>
											)}
										</div>
									</MenuList>
								</div>
							)}
						</div>
					</div>
				</div>
			</div>

			{/* Error */}
			{error && (
				<div className="px-[14px] py-[10px] bg-red-50 border border-red-200 rounded-[8px]">
					<p className="text-red-600 text-[14px] leading-[18px]">{error}</p>
				</div>
			)}

			{/* Confirm Button */}
			<AppButton
				variant="marketing"
				size="lg"
				fullWidth
				label="Connect ad account and apply"
				isLoading={loading}
				loader="dots"
				disabled={loading}
				onClick={handleConfirm}
			/>
		</div>
	)
}
